import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, CardHeader, FormField, InlineAlert } from "./primitives";
import { Spinner, SkeletonRows } from "./loading";

const PAGE_SIZE = 25;

export type CallHistoryEntry = {
  id: number;
  userId: number;
  callSid: string;
  status: string;
  resultLabel: string | null;
  resultDate: string | null;
  transcript: string | null;
  recordingAvailable: boolean;
  voiceProvider: string | null;
  errorReason: string | null;
  startedAt: string;
};

export type CallHistoryLoadResult =
  | { ok: true; calls: CallHistoryEntry[] }
  | { ok: false; error: string };

export interface CallHistoryListProps {
  loadCalls: () => Promise<CallHistoryLoadResult>;
  userLabel?: (userId: number) => string;
  renderTranscript?: (text: string) => React.ReactNode;
  idPrefix?: string;
  refreshIcon?: React.ReactNode;
}

function formatDate(iso: string) {
  if (!iso) return "";
  try {
    const match = iso.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}) UTC$/);
    const date = match ? new Date(`${match[1]}T${match[2]}:00.000Z`) : new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleString();
  } catch {
    return iso;
  }
}

function matchesSearch(call: CallHistoryEntry, term: string): boolean {
  const lower = term.toLowerCase();
  return (
    call.callSid.toLowerCase().includes(lower) ||
    (call.transcript ?? "").toLowerCase().includes(lower) ||
    (call.resultLabel ?? "").toLowerCase().includes(lower) ||
    (call.errorReason ?? "").toLowerCase().includes(lower) ||
    String(call.userId).includes(lower)
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`settings-badge settings-badge-status-${status.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      {status}
    </span>
  );
}

export function CallHistoryList({
  loadCalls,
  userLabel,
  renderTranscript,
  idPrefix = "call-history",
  refreshIcon,
}: CallHistoryListProps) {
  const [allCalls, setAllCalls] = useState<CallHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await loadCalls();
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setAllCalls(result.calls);
  }, [loadCalls]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- data-fetch on mount
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    let result = allCalls;
    if (statusFilter !== "all") {
      result = result.filter((c) => c.status === statusFilter);
    }
    if (userFilter !== "all") {
      result = result.filter((c) => String(c.userId) === userFilter);
    }
    if (search.trim()) {
      result = result.filter((c) => matchesSearch(c, search.trim()));
    }
    return [...result].sort(
      (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
    );
  }, [allCalls, statusFilter, userFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageCalls = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const statuses = useMemo(() => {
    const set = new Set(allCalls.map((c) => c.status));
    return [...set].sort();
  }, [allCalls]);

  const userIds = useMemo(() => {
    const set = new Set(allCalls.map((c) => c.userId));
    return [...set].sort((a, b) => a - b);
  }, [allCalls]);

  const getUserLabel = userLabel ?? ((id: number) => `User #${id}`);

  const hasActiveFilters =
    search.trim().length > 0 || statusFilter !== "all" || userFilter !== "all";

  return (
    <Card>
      <CardHeader
        title="Call History"
        meta={`Showing ${filtered.length} of ${allCalls.length} calls`}
        action={
          <div className="shell-actions">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={!hasActiveFilters}
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
                setUserFilter("all");
              }}
            >
              Reset call history filters
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void refresh()}
              disabled={loading}
              leadingIcon={refreshIcon}
            >
              Refresh
            </Button>
          </div>
        }
      />
      <div className="card-body ds-stack">
        <div className="settings-compact-grid">
          <FormField label="Search" htmlFor={`${idPrefix}-search`}>
            <input
              id={`${idPrefix}-search`}
              type="text"
              className="cf-input"
              placeholder="Call SID, transcript, user ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FormField>
          <FormField label="Status" htmlFor={`${idPrefix}-status`}>
            <select
              id={`${idPrefix}-status`}
              className="cf-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="User" htmlFor={`${idPrefix}-user`}>
            <select
              id={`${idPrefix}-user`}
              className="cf-input"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            >
              <option value="all">All users</option>
              {userIds.map((id) => (
                <option key={id} value={String(id)}>
                  {getUserLabel(id)}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}

        <div style={{ position: "relative" }}>
          {loading && allCalls.length > 0 ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--cf-bg-subtle)",
                zIndex: 1,
                borderRadius: "6px",
              }}
            >
              <Spinner size="md" label="Refreshing..." />
            </div>
          ) : null}
          {loading && allCalls.length === 0 ? (
            <div style={{ padding: "var(--cf-space-md)" }}>
              <SkeletonRows count={4} />
            </div>
          ) : pageCalls.length === 0 ? (
            <InlineAlert tone="neutral">
              {allCalls.length === 0 ? "No calls found." : "No calls match the current filters."}
            </InlineAlert>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--cf-space-sm)",
              }}
            >
              {pageCalls.map((call) => {
                const isExpanded = expandedId === call.id;
                return (
                  <div
                    key={call.id}
                    className="card"
                    style={{ cursor: "pointer" }}
                    onClick={() => setExpandedId(isExpanded ? null : call.id)}
                  >
                    <div
                      style={{
                        display: "grid",
                        gap: "var(--cf-space-sm)",
                        gridTemplateColumns: "1fr auto",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ display: "grid", gap: "0.25rem" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: "var(--cf-space-sm)",
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <strong
                            style={{
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                              wordBreak: "break-all",
                            }}
                          >
                            {call.callSid}
                          </strong>
                          <StatusBadge status={call.status} />
                          {call.resultLabel ? (
                            <span
                              style={{
                                fontSize: "var(--cf-space-12)",
                                padding: "0.15rem 0.5rem",
                                borderRadius: "9999px",
                                background:
                                  "color-mix(in srgb, var(--cf-info), var(--cf-panel) 18%)",
                                color: "var(--cf-text)",
                                fontWeight: 600,
                              }}
                            >
                              {call.resultLabel}
                            </span>
                          ) : null}
                        </div>
                        <div
                          style={{
                            fontSize: "0.8rem",
                            color: "var(--cf-muted-text)",
                            display: "flex",
                            gap: "var(--cf-space-md)",
                            flexWrap: "wrap",
                          }}
                        >
                          <span>ID: {call.id}</span>
                          <span>{getUserLabel(call.userId)}</span>
                          <span>{formatDate(call.startedAt)}</span>
                          {call.resultDate ? <span>Result: {call.resultDate}</span> : null}
                          {call.voiceProvider ? <span>Provider: {call.voiceProvider}</span> : null}
                          {call.recordingAvailable ? <span>Has recording</span> : null}
                          {call.errorReason ? (
                            <span style={{ color: "var(--cf-error)" }}>
                              Error: {call.errorReason}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--cf-muted-text)",
                        }}
                      >
                        {isExpanded ? "▲" : "▼"}
                      </span>
                    </div>
                    {isExpanded ? (
                      <div
                        style={{
                          marginTop: "var(--cf-space-12)",
                          paddingTop: "var(--cf-space-12)",
                          borderTop: "1px solid var(--cf-border)",
                          fontSize: "0.85rem",
                        }}
                      >
                        {call.resultDate ? (
                          <p style={{ margin: "0 0 0.5rem" }}>
                            <strong>Result date:</strong> {formatDate(call.resultDate)}
                          </p>
                        ) : null}
                        {call.transcript ? (
                          <div>
                            <strong>Transcript:</strong>
                            <div
                              style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                marginTop: "0.25rem",
                                padding: "var(--cf-space-sm)",
                                background: "var(--cf-bg-subtle)",
                                borderRadius: "6px",
                                fontSize: "0.8rem",
                                maxHeight: "200px",
                                overflow: "auto",
                                fontFamily: "monospace",
                              }}
                            >
                              {renderTranscript
                                ? renderTranscript(call.transcript)
                                : call.transcript}
                            </div>
                          </div>
                        ) : (
                          <p
                            style={{
                              color: "var(--cf-text-muted)",
                              fontSize: "13px",
                              margin: 0,
                            }}
                          >
                            No transcript available.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {totalPages > 1 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "var(--cf-space-sm)",
              marginTop: "var(--cf-space-12)",
            }}
          >
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <span style={{ color: "var(--cf-text-muted)", fontSize: "13px" }}>
              Page {page + 1} of {totalPages}
            </span>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
